// src/utils/petSimilarityHelper.ts
// Helper functions for checking pet name similarity with Levenshtein distance
// Checks within a client's existing pets to prevent duplicate pet names

import { Pet } from '../services/api';

/**
 * Normalizes a pet name for comparison
 * - Converts to lowercase
 * - Removes extra whitespace
 * - Removes special characters
 */
const normalizePetName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();
};

/**
 * Calculate Levenshtein distance between two strings
 * Returns the minimum number of edits (insertions, deletions, substitutions) needed
 */
const levenshteinDistance = (str1: string, str2: string): number => {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix: number[][] = [];

  // Initialize matrix
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  // Fill matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j] + 1 // deletion
        );
      }
    }
  }

  return matrix[len1][len2];
};

/**
 * Calculate similarity percentage between two strings
 * Returns a value between 0 (completely different) and 1 (identical)
 */
const calculateSimilarity = (str1: string, str2: string): number => {
  const distance = levenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);

  if (maxLength === 0) return 1;

  return 1 - distance / maxLength;
};

/**
 * Enhanced string similarity check for pet names
 * Returns true if the normalized names are very similar
 */
const areNamesSimilar = (name1: string, name2: string): boolean => {
  const normalized1 = normalizePetName(name1);
  const normalized2 = normalizePetName(name2);

  // Exact match after normalization
  if (normalized1 === normalized2) {
    return true;
  }

  // Skip if either name is too short after normalization
  if (normalized1.length < 2 || normalized2.length < 2) {
    return false;
  }

  // Check if one contains the other
  if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) {
    return true;
  }

  // Calculate similarity using Levenshtein distance
  // Trigger warning if similarity is > 70%
  const similarity = calculateSimilarity(normalized1, normalized2);
  const SIMILARITY_THRESHOLD = 0.6; // 70% similar = show warning
  // SETTINGS:
  // 70% (current) = Moderately strict
  // 80% = More strict (fewer warnings, but might miss some)
  // 60% = Less strict (more warnings, catches more variations)

  if (similarity >= SIMILARITY_THRESHOLD) {
    return true;
  }

  return false;
};

/**
 * Finds similar pets from a client's existing pets list
 * Returns array of pets with similar names
 * @param newPetName - The name of the new pet being created
 * @param existingPets - The client's existing pets (siblings)
 * @param excludePetId - Optional: Pet ID to exclude (for edit mode)
 */
export const findSimilarPets = (
  newPetName: string,
  existingPets: Pet[],
  excludePetId?: number
): Pet[] => {
  if (!newPetName || newPetName.trim().length < 2) {
    return [];
  }

  return existingPets.filter((pet) => {
    // Exclude the pet being edited
    if (excludePetId && pet.id === excludePetId) {
      return false;
    }
    return areNamesSimilar(newPetName, pet.name);
  });
};

/**
 * Checks if a pet name is similar to any of the client's existing pets
 * Returns the first similar pet found, or null
 * @param newPetName - The name of the new pet being created
 * @param existingPets - The client's existing pets (siblings)
 * @param excludePetId - Optional: Pet ID to exclude (for edit mode)
 */
export const findFirstSimilarPet = (
  newPetName: string,
  existingPets: Pet[],
  excludePetId?: number
): Pet | null => {
  const similar = findSimilarPets(newPetName, existingPets, excludePetId);
  return similar.length > 0 ? similar[0] : null;
};
